/**
 * Hierarchy Field Hooks
 *
 * Purpose: Custom React hooks for hierarchical field management
 * Architecture: State management hooks for tree navigation, selection, and data loading
 * Author: Development Team
 * Created: 2025-01-21
 *
 * Hooks:
 * - useHierarchyData: Manage hierarchy chain and field definition caching
 * - useTreeNavigation: Manage tree state, expansion, and node loading
 * - useNodeSelection: Manage node selection and value loading
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { asList, isAll } from '../utils/listValues';
import { mdmService } from '../services/mdmService';
import { buildHierarchyChain, valueToTreeNode } from '../utils/hierarchyHelpers';
import type { TreeNode } from '../types/hierarchy';
import type { MasterDataValue } from '../types/mdm';
import type { FieldDefinition } from '../services/metadataService';
import type { PermissionKey } from '../constants/permissionKeys';

/**
 * Return type for useHierarchyData hook
 */
export interface UseHierarchyDataReturn {
  hierarchyChain: FieldDefinition[];
  targetField: FieldDefinition | null;
  maxLevel: number;
  loading: boolean;
  error: string | null;
  loadHierarchyChain: () => Promise<void>;
}

/**
 * useHierarchyData Hook
 *
 * Purpose: Manage hierarchy chain loading and field definition caching
 * Logic:
 * - Loads hierarchy chain from target field to root on mount
 * - Caches field definitions to avoid redundant API calls
 * - Provides hierarchy chain for tree navigation
 *
 * Input:
 * @param fielddefId - Target field definition ID
 * @param tenantId - Tenant context
 *
 * Output:
 * @returns UseHierarchyDataReturn - Hierarchy chain and loading state
 *
 * Example:
 * ```tsx
 * const { hierarchyChain, maxLevel, loading } = useHierarchyData('fd_country', 'GLOBAL');
 * ```
 */
export function useHierarchyData(fielddefId: string, tenantId: string): UseHierarchyDataReturn {
  const [hierarchyChain, setHierarchyChain] = useState<FieldDefinition[]>([]);
  const [targetField, setTargetField] = useState<FieldDefinition | null>(null);
  const [maxLevel, setMaxLevel] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Cache for field definitions
  const fieldDefCacheRef = useRef<Map<string, FieldDefinition>>(new Map());

  const loadHierarchyChain = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const chain = await buildHierarchyChain(fielddefId, tenantId, fieldDefCacheRef.current);

      setHierarchyChain(chain.fieldDefinitions);
      setTargetField(chain.targetField);
      setMaxLevel(chain.maxLevel);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load hierarchy configuration';
      console.error('[useHierarchyData] Error loading hierarchy chain:', err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [fielddefId, tenantId]);

  // Load hierarchy chain on mount
  useEffect(() => {
    loadHierarchyChain();
  }, [loadHierarchyChain]);

  return {
    hierarchyChain,
    targetField,
    maxLevel,
    loading,
    error,
    loadHierarchyChain,
  };
}

/**
 * Return type for useTreeNavigation hook
 */
export interface UseTreeNavigationReturn {
  rootNodes: TreeNode[];
  expandedNodes: string[];
  searchTerm: string;
  searchMatchIds: string[];
  loading: boolean;
  error: string | null;
  setExpandedNodes: (nodes: string[]) => void;
  setSearchTerm: (term: string) => void;
  setSearchMatchIds: (ids: string[]) => void;
  loadRootNodes: () => Promise<void>;
  loadNodeChildren: (node: TreeNode) => Promise<void>;
  handleNodeExpand: (nodeId: string) => Promise<void>;
  /** ORCH-418: load and open every group in the tree */
  expandAll: () => Promise<void>;
  handleSearch: (search: string) => Promise<void>;
  resetTree: () => void;
  nodeCache: Map<string, TreeNode>;
}

/**
 * useTreeNavigation Hook
 *
 * Purpose: Manage tree state, expansion, and node loading
 * Logic:
 * - Loads root nodes from first level of hierarchy
 * - Manages node expansion state
 * - Lazy loads children when nodes are expanded
 * - Caches loaded nodes for performance
 * - Supports search with expansion to matches
 *
 * Input:
 * @param hierarchyChain - Array of field definitions from root to target
 * @param tenantId - Tenant context
 * @param enabled - Whether to enable loading (default: true)
 *
 * Output:
 * @returns UseTreeNavigationReturn - Tree state and navigation functions
 *
 * Example:
 * ```tsx
 * const { rootNodes, loadRootNodes, handleNodeExpand } = useTreeNavigation(hierarchyChain, 'GLOBAL');
 * ```
 */
export function useTreeNavigation(
  hierarchyChain: FieldDefinition[],
  tenantId: string,
  enabled: boolean = true,
  permission: PermissionKey | null = null,
  filterBy: string[] = []
): UseTreeNavigationReturn {
  const [rootNodes, setRootNodes] = useState<TreeNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchMatchIds, setSearchMatchIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Cache for loaded nodes
  const nodeCacheRef = useRef<Map<string, TreeNode>>(new Map());

  /**
   * Fetch values for a specific field definition and parent
   */
  const fetchValuesForParent = useCallback(
    async (fielddefId: string, parentId: string | null): Promise<MasterDataValue[]> => {
      try {
        const values = await mdmService.getValues({
          fielddef_id: fielddefId,
          tenant_id: tenantId,
          parent_value_id: parentId || undefined,
          include_inactive: false,
          include_deleted: false,
          // ORCH-332: only what the form's permission reaches, ancestors not selectable
          for_permission: permission ?? undefined,
          // ORCH-366: the tree's own data is the offer request, scoped or not
          offer: true,
          // ORCH-400: narrowed by the form's related fields (subset ancestors stay as navigation)
          ...(filterBy.length ? { filter_by: filterBy } : {}),
        });

        return values;
      } catch (err: any) {
        console.error('[useTreeNavigation] Failed to fetch MDM values:', err);
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, permission, filterBy.join('|')]
  );

  /**
   * Load root nodes from first level of hierarchy
   */
  const loadRootNodes = useCallback(async () => {
    if (!enabled || hierarchyChain.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      const rootFieldDef = hierarchyChain[0];
      const rootValues = await fetchValuesForParent(rootFieldDef.fielddef_id, null);

      if (!rootValues || rootValues.length === 0) {
        setError('No root values found');
        setLoading(false);
        return;
      }

      const nodes = rootValues
        .filter((v) => v && v.id && v.name)
        .map((v) => valueToTreeNode(v, 0, rootFieldDef.fielddef_id));

      // Cache nodes
      nodes.forEach((node) => {
        nodeCacheRef.current.set(node.id, node);
      });

      setRootNodes(nodes);
    } catch (err: any) {
      console.error('[useTreeNavigation] Failed to load root nodes:', err);
      setError(err.message || 'Failed to load hierarchy');
    } finally {
      setLoading(false);
    }
  }, [enabled, hierarchyChain, fetchValuesForParent]);

  /**
   * Load children for a node
   */
  const loadNodeChildren = useCallback(
    async (node: TreeNode) => {
      if (node.loaded || node.loading) return;

      // Mark as loading
      node.loading = true;
      setRootNodes((prev) => [...prev]); // Force re-render

      try {
        const currentLevelIndex = hierarchyChain.findIndex(
          (fd) => fd.fielddef_id === node.fielddef_id
        );

        // Check if leaf node
        if (currentLevelIndex < 0 || currentLevelIndex >= hierarchyChain.length - 1) {
          node.loaded = true;
          node.loading = false;
          setRootNodes((prev) => [...prev]);
          return;
        }

        const nextFieldDef = hierarchyChain[currentLevelIndex + 1];
        const children = await fetchValuesForParent(nextFieldDef.fielddef_id, node.value.id);

        const validChildren = children
          .filter((v) => v && v.id && v.name)
          .map((v) => valueToTreeNode(v, node.level + 1, nextFieldDef.fielddef_id));

        node.children = validChildren;
        node.loaded = true;
        node.loading = false;

        // Cache children
        validChildren.forEach((child) => {
          if (child && child.id) {
            nodeCacheRef.current.set(child.id, child);
          }
        });

        setRootNodes((prev) => [...prev]); // Force re-render
      } catch (err: any) {
        console.error(`[useTreeNavigation] Failed to load children for node ${node.id}:`, err);
        node.loading = false;
        setRootNodes((prev) => [...prev]);
      }
    },
    [hierarchyChain, fetchValuesForParent]
  );

  /**
   * Handle node expand
   */
  const handleNodeExpand = useCallback(
    async (nodeId: string) => {
      const node = nodeCacheRef.current.get(nodeId);
      if (!node) {
        console.warn('[useTreeNavigation] Node not found in cache:', nodeId);
        return;
      }
      if (node.loaded || node.loading) return;

      await loadNodeChildren(node);
    },
    [loadNodeChildren]
  );

  /**
   * Expand every group in the tree (ORCH-418 "Expand All")
   *
   * Logic: level by level from the roots, load the children of every group not yet loaded
   *        (one level's fetches run in parallel), then expand every group found. Nodes are
   *        mutated in place by loadNodeChildren, so the walk sees children as they land.
   *        Leaves (the last level of the chain) are never fetched.
   */
  const expandAll = useCallback(async () => {
    const groupIds: string[] = [];
    let level: TreeNode[] = rootNodes;
    while (level.length > 0) {
      const groups = level.filter((node) => node.level < hierarchyChain.length - 1);
      if (groups.length === 0) break;
      await Promise.all(groups.filter((node) => !node.loaded).map(loadNodeChildren));
      groups.forEach((group) => groupIds.push(group.id));
      level = groups.flatMap((group) => group.children);
    }
    setExpandedNodes(groupIds);
  }, [rootNodes, hierarchyChain, loadNodeChildren]);

  /**
   * Handle search across all hierarchy levels
   */
  const handleSearch = useCallback(
    async (search: string) => {
      console.log('[useTreeNavigation] Search triggered:', search);
      setSearchTerm(search);

      if (!search.trim()) {
        console.log('[useTreeNavigation] Empty search, clearing expansion');
        setExpandedNodes([]);
        setSearchMatchIds([]);
        return;
      }

      try {
        const allFieldDefIds = hierarchyChain.map((fd) => fd.fielddef_id);
        console.log('[useTreeNavigation] Searching across hierarchy levels:', allFieldDefIds);

        // Search across all levels
        const searchPromises = allFieldDefIds.map(async (fielddefId) => {
          const values = await mdmService.searchValues(
            search,
            fielddefId,
            tenantId,
            undefined,
            permission ?? undefined,
            true, // ORCH-366: the offer request
            // ORCH-400: the picker's related-field constraints, only when it has any
            ...(filterBy.length ? [filterBy] : [])
          );
          return values.map((v) => ({ value: v, fielddefId }));
        });

        const results = await Promise.all(searchPromises);
        const matches = results.flat();
        console.log(`[useTreeNavigation] Total matches found: ${matches.length}`);

        if (matches.length > 0) {
          const pathsToExpand = new Set<string>();
          const hierarchyPaths: Array<Array<{ id: string; fielddef_id: string }>> = [];
          const matchIds: string[] = [];

          matches.forEach((match) => {
            const { value } = match;
            matchIds.push(value.id);

            if (value.hierarchy_path) {
              hierarchyPaths.push(value.hierarchy_path);
              value.hierarchy_path.forEach((item) => {
                pathsToExpand.add(item.id);
              });
            }

            pathsToExpand.add(value.id);
          });

          console.log(`[useTreeNavigation] Will expand ${pathsToExpand.size} nodes`);

          // Load hierarchy paths
          for (const path of hierarchyPaths) {
            for (let i = 0; i < path.length - 1; i++) {
              const nodeId = path[i].id;
              await handleNodeExpand(nodeId).catch((err) => {
                console.error('[useTreeNavigation] Error loading children for node:', nodeId, err);
              });
            }
          }

          setExpandedNodes(Array.from(pathsToExpand));
          setSearchMatchIds(matchIds);
        } else {
          console.log('[useTreeNavigation] No matches found');
          setSearchMatchIds([]);
        }
      } catch (err: any) {
        console.error('[useTreeNavigation] Search failed:', err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hierarchyChain, tenantId, handleNodeExpand, permission, filterBy.join('|')]
  );

  /**
   * Reset tree state
   */
  const resetTree = useCallback(() => {
    setRootNodes([]);
    setExpandedNodes([]);
    setSearchTerm('');
    setSearchMatchIds([]);
    nodeCacheRef.current.clear();
  }, []);

  return {
    rootNodes,
    expandedNodes,
    searchTerm,
    searchMatchIds,
    loading,
    error,
    setExpandedNodes,
    setSearchTerm,
    setSearchMatchIds,
    loadRootNodes,
    loadNodeChildren,
    handleNodeExpand,
    expandAll,
    handleSearch,
    resetTree,
    nodeCache: nodeCacheRef.current,
  };
}

/**
 * Return type for useNodeSelection hook
 */
export interface UseNodeSelectionReturn {
  selectedNodeIds: string[];
  selectedValues: MasterDataValue[];
  setSelectedNodeIds: (ids: string[]) => void;
  loadInitialValue: () => Promise<void>;
  handleApply: (
    nodeCache: Map<string, TreeNode>,
    isMultiselect: boolean,
    onChange: (value: any) => void
  ) => void;
  handleRemove: (valueId: string, isMultiselect: boolean, onChange: (value: any) => void) => void;
}

/**
 * useNodeSelection Hook
 *
 * Purpose: Manage node selection and value loading
 * Logic:
 * - Loads initial values with hierarchy paths
 * - Manages selected node IDs
 * - Optimizes selection by removing descendants
 * - Handles apply and remove operations
 *
 * Input:
 * @param value - Current form value (ID or array of IDs)
 * @param isMultiselect - Whether field allows multiple selections
 * @param tenantId - Tenant context
 *
 * Output:
 * @returns UseNodeSelectionReturn - Selection state and functions
 *
 * Example:
 * ```tsx
 * const { selectedValues, handleApply } = useNodeSelection(value, isMultiselect, 'GLOBAL');
 * ```
 */
export function useNodeSelection(
  value: any,
  isMultiselect: boolean,
  tenantId: string
): UseNodeSelectionReturn {
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<MasterDataValue[]>([]);

  // Track initial value loading
  const initialValueLoadedRef = useRef(false);
  const lastLoadedValueRef = useRef<any>(undefined);

  /**
   * Load initial value with hierarchy path
   */
  const loadInitialValue = useCallback(async () => {
    if (lastLoadedValueRef.current === value && initialValueLoadedRef.current) {
      return;
    }

    // ALL is not a master-data value: the renderer shows it as one "All" chip (ORCH-418)
    if (!value || isAll(value)) {
      setSelectedValues([]);
      setSelectedNodeIds([]);
      initialValueLoadedRef.current = true;
      lastLoadedValueRef.current = value;
      return;
    }

    initialValueLoadedRef.current = true;

    try {
      // The stored value may be in either shape (ORCH-354)
      const values = await Promise.all(
        asList(value).map((id) =>
          mdmService.getValue(id, {
            include_hierarchy_path: true,
            tenant_id: tenantId,
          })
        )
      );
      setSelectedValues(values);
      setSelectedNodeIds(values.map((v) => v.id));
      lastLoadedValueRef.current = value;
    } catch (err: any) {
      console.error('[useNodeSelection] Failed to load initial value:', err);
      initialValueLoadedRef.current = false;
      lastLoadedValueRef.current = undefined;
    }
  }, [value, isMultiselect, tenantId]);

  /**
   * Apply selection and update form value
   */
  const handleApply = useCallback(
    (nodeCache: Map<string, TreeNode>, isMultiselect: boolean, onChange: (value: any) => void) => {
      try {
        // Get selected values from cache. A value already on the record may be outside
        // a scoped tree (ORCH-332) and never loaded into it: keep it, from the values
        // loaded with the record, rather than dropping it on Apply.
        const selected = selectedNodeIds
          .map((id) => nodeCache.get(id)?.value ?? selectedValues.find((v) => v.id === id))
          .filter((v): v is MasterDataValue => v !== undefined);

        // Optimize: Remove descendants if ancestor is selected
        const optimized: MasterDataValue[] = [];
        for (const val of selected) {
          const isDescendant = selected.some((other) => {
            if (other.id === val.id) return false;
            return val.hierarchy_path?.some((item) => item.id === other.id);
          });
          if (!isDescendant) {
            optimized.push(val);
          }
        }

        setSelectedValues(optimized);

        // Update form value
        if (isMultiselect) {
          onChange(optimized.map((v) => v.id));
        } else {
          onChange(optimized.length > 0 ? optimized[0].id : null);
        }
      } catch (err: any) {
        console.error('[useNodeSelection] Failed to apply selection:', err);
      }
    },
    [selectedNodeIds, selectedValues]
  );

  /**
   * Remove selected value
   */
  const handleRemove = useCallback(
    (valueId: string, isMultiselect: boolean, onChange: (value: any) => void) => {
      const newSelected = selectedValues.filter((v) => v.id !== valueId);
      setSelectedValues(newSelected);
      setSelectedNodeIds(newSelected.map((v) => v.id));

      if (isMultiselect) {
        onChange(newSelected.map((v) => v.id));
      } else {
        onChange(null);
      }
    },
    [selectedValues]
  );

  // Load initial value on mount
  useEffect(() => {
    loadInitialValue();
  }, [loadInitialValue]);

  return {
    selectedNodeIds,
    selectedValues,
    setSelectedNodeIds,
    loadInitialValue,
    handleApply,
    handleRemove,
  };
}
